import type { StagedEntity } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import {
  DATASOURCE_TYPE_DISPLAY_NAMES,
  STATEMENT_TYPE_DISPLAY_NAMES,
  DB_DATASOURCE_TYPES,
  CSHARP_DATASOURCE_TYPES,
  REST_DATASOURCE_TYPES,
  EDatasourceType
} from "@kuika/kuika-cl-model/src/enums/datasource-type";
import { EJsonEntityType } from "@kuika/kuika-cl-model/src/enums/entity-type";
import type {
  EntityBadgeHue,
  EntityDiffConfig,
  EntityDiffField,
  FieldRenderType,
  ParamRow,
  KeyValuePair
} from "./entity-diff-types";

// ── Snapshot parsing ──

export function parseSnapshot(snapshot: unknown): Record<string, unknown> | null {
  if (!snapshot) return null;
  if (typeof snapshot === "string") {
    try {
      return JSON.parse(snapshot) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  const obj = snapshot as Record<string, unknown>;
  const raw = obj.JSON ?? obj.Json ?? obj.json;
  if (raw && typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (raw && typeof raw === "object") return raw as Record<string, unknown>;
  return obj;
}

function parseJsonField(value: unknown): Record<string, unknown> | unknown[] | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof value === "object") return value as Record<string, unknown>;
  return null;
}

function str(value: unknown): string {
  if (value == null) return "";
  return String(value);
}

function resolveDsTypeName(typeValue: unknown): string {
  if (typeValue == null) return "Unknown";
  const n = typeof typeValue === "number" ? typeValue : parseInt(String(typeValue), 10);
  return DATASOURCE_TYPE_DISPLAY_NAMES[n] ?? `Type ${n}`;
}

function maskConnectionString(cs: string): string {
  if (!cs) return "";
  return cs.replace(/(Password|Pwd)\s*=\s*[^;]+/gi, "$1=***");
}

// ── Helpers for structured data ──

function extractParamRows(params: unknown): ParamRow[] {
  if (!Array.isArray(params)) return [];
  return params
    .slice(0, 20)
    .map((p: Record<string, unknown>) => ({
      name: str(p.name ?? p.parameterName ?? p.Name ?? ""),
      type: str(p.Typename ?? p.typename ?? p.type ?? p.Type ?? ""),
      required: p.isMandatory === true || p.isRequired === true,
      defaultValue: str(p.defaultValue ?? p.DefaultValue ?? "")
    }))
    .filter((r) => r.name);
}

function extractConnectionKv(data: Record<string, unknown>): KeyValuePair[] {
  const cs = str(data.ConnectionString ?? data.connectionString ?? "");
  if (!cs) return [];
  return cs
    .split(";")
    .filter(Boolean)
    .map((part) => {
      const eqIdx = part.indexOf("=");
      if (eqIdx === -1) return { key: part.trim(), value: "" };
      const k = part.substring(0, eqIdx).trim();
      const v = part.substring(eqIdx + 1).trim();
      const masked = /^(password|pwd)$/i.test(k);
      return { key: k, value: masked ? "***" : v, masked };
    });
}

// ── Config per entity type ──

const ENTITY_CONFIGS: Record<number, EntityDiffConfig> = {
  [EJsonEntityType.KAction]: { entityType: EJsonEntityType.KAction, resolutionLevel: "whole" },
  [EJsonEntityType.KDatasource]: { entityType: EJsonEntityType.KDatasource, resolutionLevel: "whole" },
  [EJsonEntityType.KProject]: { entityType: EJsonEntityType.KProject, resolutionLevel: "whole" },
  [EJsonEntityType.KWorkFlow]: {
    entityType: EJsonEntityType.KWorkFlow,
    resolutionLevel: "field",
    linkedGroups: [["json", "xml"]]
  },
  [EJsonEntityType.KApiManagement]: {
    entityType: EJsonEntityType.KApiManagement,
    resolutionLevel: "field",
    linkedGroups: [["json", "xml"]]
  },
  [EJsonEntityType.KNotificationTemplate]: {
    entityType: EJsonEntityType.KNotificationTemplate,
    resolutionLevel: "whole"
  },
  [EJsonEntityType.KNotificationLanguageTemplate]: {
    entityType: EJsonEntityType.KNotificationLanguageTemplate,
    resolutionLevel: "whole"
  },
  [EJsonEntityType.KScheduledJobs]: {
    entityType: EJsonEntityType.KScheduledJobs,
    resolutionLevel: "whole",
    linkedGroups: [["json", "xml"]]
  },
  [EJsonEntityType.KOfflineTable]: { entityType: EJsonEntityType.KOfflineTable, resolutionLevel: "whole" }
};

export function getEntityDiffConfig(entityType: number): EntityDiffConfig {
  return ENTITY_CONFIGS[entityType] ?? { entityType, resolutionLevel: "whole" };
}

// ── Field extraction per entity type ──

interface ExtractedField {
  key: string;
  label: string;
  value: string;
  renderType: FieldRenderType;
  language?: string;
  linkedGroup?: string;
  structuredData?: ParamRow[] | KeyValuePair[] | string[];
  badgeHue?: EntityBadgeHue;
}

interface ExtractedFields {
  typeName: string;
  name: string;
  parentInfo?: string;
  fields: ExtractedField[];
}

function extractActionFields(data: Record<string, unknown>, allStaged?: StagedEntity[]): ExtractedFields {
  const actionType = data.ActionType ?? data.actionType;
  const actionTypeNum = typeof actionType === "number" ? actionType : parseInt(str(actionType), 10);

  // KAction snapshots frequently omit ActionType (the type lives on the parent datasource), so infer
  // the kind from the content present. Without this, classification falls back to "Unknown Action"
  // and the SQL/C# drops into the raw generic field dump instead of a proper code diff.
  const statementText = str(data.Statement ?? data.statement ?? "");
  const codeText = str(data.Code ?? data.code ?? "");
  const isDbAction =
    DB_DATASOURCE_TYPES.has(actionTypeNum) ||
    data.IsDbAction === true ||
    data.isDbAction === true ||
    data.IsManagedDbAction === true ||
    data.isManagedDbAction === true ||
    statementText !== "";
  const isCsharpAction = !isDbAction && (CSHARP_DATASOURCE_TYPES.has(actionTypeNum) || codeText !== "");

  const resolvedTypeName = resolveDsTypeName(actionType);
  const hasKnownType = resolvedTypeName !== "Unknown" && !resolvedTypeName.startsWith("Type ");
  let typeName: string;
  if (hasKnownType) typeName = `${resolvedTypeName} Action`;
  else if (isDbAction) typeName = "SQL Action";
  else if (isCsharpAction) typeName = "C# Action";
  else typeName = "Action";

  const dsId = str(data.KDatasource_Id ?? data.kDatasource_Id ?? "");
  let parentInfo: string | undefined;
  if (dsId && allStaged) {
    const parentDs = allStaged.find(
      (e) => String(e.entityType) === String(EJsonEntityType.KDatasource) && e.entityId === dsId
    );
    if (parentDs) {
      const parentData = parseSnapshot(parentDs.afterSnapshot ?? parentDs.beforeSnapshot);
      if (parentData) {
        parentInfo = `${str(parentData.Name ?? parentData.name)} (${resolveDsTypeName(parentData.DatasourceType ?? parentData.datasourceType)})`;
      }
    }
  }

  const fields: ExtractedField[] = [];

  const paramStr = str(data.Parameters ?? data.parameters ?? "");
  const params = parseJsonField(paramStr);
  const paramRows = extractParamRows(params);

  if (isDbAction) {
    const tableName = str(data.TableName ?? data.tableName ?? "");
    if (tableName)
      fields.push({ key: "table", label: "Table", value: tableName, renderType: "badge", badgeHue: "blue" });
    const stmtType = data.StatementType ?? data.statementType;
    if (stmtType != null) {
      const stmtTypeNum = typeof stmtType === "number" ? stmtType : parseInt(str(stmtType), 10);
      fields.push({
        key: "stmtType",
        label: "Statement Type",
        value: STATEMENT_TYPE_DISPLAY_NAMES[stmtTypeNum] ?? str(stmtType),
        renderType: "badge",
        badgeHue: "purple"
      });
    }
    if (statementText)
      fields.push({
        key: "statement",
        label: "SQL Statement",
        value: statementText,
        renderType: "code",
        language: "sql"
      });
  } else if (isCsharpAction) {
    if (codeText)
      fields.push({ key: "code", label: "C# Code", value: codeText, renderType: "code", language: "csharp" });
  } else if (REST_DATASOURCE_TYPES.has(actionTypeNum)) {
    // REST-specific: show params as table
  } else if (actionTypeNum === EDatasourceType.Blockly) {
    const blocklyInfo = parseJsonField(data.BlocklyActionInfo ?? data.blocklyActionInfo);
    if (blocklyInfo && !Array.isArray(blocklyInfo)) {
      const connectedAction = str((blocklyInfo as Record<string, unknown>).connectedActionName ?? "");
      if (connectedAction)
        fields.push({ key: "connectedAction", label: "Connected Action", value: connectedAction, renderType: "text" });
    }
  } else if (actionTypeNum === EDatasourceType.AIActions) {
    const aiInfo = parseJsonField(data.AiActionInfo ?? data.aiActionInfo);
    if (aiInfo && !Array.isArray(aiInfo)) {
      const ai = aiInfo as Record<string, unknown>;
      if (ai.model)
        fields.push({ key: "model", label: "Model", value: str(ai.model), renderType: "badge", badgeHue: "purple" });
      const prompt = str(ai.prompt ?? ai.systemPrompt ?? "");
      if (prompt) fields.push({ key: "prompt", label: "Prompt", value: prompt, renderType: "code", language: "text" });
    }
  } else if (actionTypeNum === EDatasourceType.AIAgent) {
    const agentInfo = parseJsonField(data.AiAgentInfo ?? data.aiAgentInfo);
    if (agentInfo && !Array.isArray(agentInfo)) {
      const agent = agentInfo as Record<string, unknown>;
      if (agent.name) fields.push({ key: "agent", label: "Agent", value: str(agent.name), renderType: "text" });
    }
  } else if (actionTypeNum === EDatasourceType.SAP) {
    const sapInfo = parseJsonField(data.SapActionInfo ?? data.sapActionInfo);
    if (sapInfo && !Array.isArray(sapInfo)) {
      const sap = sapInfo as Record<string, unknown>;
      if (sap.functionModule ?? sap.FunctionModule) {
        fields.push({
          key: "sapFn",
          label: "Function Module",
          value: str(sap.functionModule ?? sap.FunctionModule),
          renderType: "text"
        });
      }
    }
  } else if (actionTypeNum === EDatasourceType.VisionAction) {
    const visionInfo = parseJsonField(data.VisionInfo ?? data.visionInfo);
    if (visionInfo && !Array.isArray(visionInfo)) {
      const vision = visionInfo as Record<string, unknown>;
      if (vision.description)
        fields.push({ key: "visionDesc", label: "Description", value: str(vision.description), renderType: "text" });
    }
  }

  const returnType = str(data.ReturnTypeName ?? data.returnTypeName ?? "");
  if (returnType) {
    const cardinality = str(data.ReturnCardinality ?? data.returnCardinality ?? "");
    fields.push({
      key: "returns",
      label: "Returns",
      value: returnType + (cardinality ? ` (${cardinality})` : ""),
      renderType: "badge",
      badgeHue: "green"
    });
  }

  if (paramRows.length > 0) {
    fields.push({
      key: "params",
      label: "Parameters",
      value: `${paramRows.length} parameter(s)`,
      renderType: "params-table",
      structuredData: paramRows
    });
  }

  return {
    typeName,
    name: str(data.Name ?? data.name ?? ""),
    parentInfo: parentInfo ? `Datasource: ${parentInfo}` : undefined,
    fields
  };
}

function extractDatasourceFields(data: Record<string, unknown>, allStaged?: StagedEntity[]): ExtractedFields {
  const dsType = data.DatasourceType ?? data.datasourceType;
  const dsTypeNum = typeof dsType === "number" ? dsType : parseInt(str(dsType), 10);
  const typeName = resolveDsTypeName(dsType);
  const fields: ExtractedField[] = [];

  fields.push({ key: "type", label: "Type", value: typeName, renderType: "badge", badgeHue: "blue" });

  if (DB_DATASOURCE_TYPES.has(dsTypeNum)) {
    const connKv = extractConnectionKv(data);
    if (connKv.length > 0) {
      fields.push({
        key: "connection",
        label: "Connection",
        value: maskConnectionString(str(data.ConnectionString ?? data.connectionString ?? "")),
        renderType: "key-value",
        structuredData: connKv
      });
    }
  } else if (REST_DATASOURCE_TYPES.has(dsTypeNum)) {
    const restConn = parseJsonField(data.RestApiConnection ?? data.restApiConnection);
    if (restConn && !Array.isArray(restConn)) {
      const rc = restConn as Record<string, unknown>;
      const kvPairs: KeyValuePair[] = [];
      if (rc.baseUrl ?? rc.BaseUrl) kvPairs.push({ key: "Base URL", value: str(rc.baseUrl ?? rc.BaseUrl) });
      if (rc.authType ?? rc.AuthType) kvPairs.push({ key: "Auth", value: str(rc.authType ?? rc.AuthType) });
      if (kvPairs.length > 0) {
        fields.push({
          key: "restConn",
          label: "REST Connection",
          value: kvPairs.map((p) => `${p.key}: ${p.value}`).join(", "),
          renderType: "key-value",
          structuredData: kvPairs
        });
      }
    }
  } else if (dsTypeNum === EDatasourceType.OData) {
    const odataConn = parseJsonField(data.ODataConnection ?? data.odataConnection);
    if (odataConn && !Array.isArray(odataConn)) {
      const oc = odataConn as Record<string, unknown>;
      if (oc.serviceUrl ?? oc.ServiceUrl)
        fields.push({
          key: "serviceUrl",
          label: "Service URL",
          value: str(oc.serviceUrl ?? oc.ServiceUrl),
          renderType: "text"
        });
    }
  } else if (CSHARP_DATASOURCE_TYPES.has(dsTypeNum)) {
    const code = str(data.Code ?? data.code ?? "");
    if (code) fields.push({ key: "code", label: "C# Code", value: code, renderType: "code", language: "csharp" });
  } else if (dsTypeNum === EDatasourceType.Mqtt) {
    const mqttInfo = parseJsonField(data.MqttConnectionInfo ?? data.mqttConnectionInfo);
    if (mqttInfo && !Array.isArray(mqttInfo)) {
      const mq = mqttInfo as Record<string, unknown>;
      if (mq.brokerUrl ?? mq.BrokerUrl)
        fields.push({
          key: "broker",
          label: "Broker URL",
          value: str(mq.brokerUrl ?? mq.BrokerUrl),
          renderType: "text"
        });
    }
  }

  if (allStaged) {
    const dsId = str(data.Id ?? data.id ?? "");
    const childActions = allStaged.filter((e) => {
      if (String(e.entityType) !== String(EJsonEntityType.KAction)) return false;
      const actionData = parseSnapshot(e.afterSnapshot ?? e.beforeSnapshot);
      if (!actionData) return false;
      return str(actionData.KDatasource_Id ?? actionData.kDatasource_Id) === dsId;
    });
    if (childActions.length > 0) {
      const actionNames = childActions.slice(0, 10).map((a) => {
        const ad = parseSnapshot(a.afterSnapshot ?? a.beforeSnapshot);
        return ad ? str(ad.Name ?? ad.name ?? a.entityId) : a.entityId;
      });
      fields.push({
        key: "actions",
        label: "Staged Actions",
        value: `${childActions.length} action(s)`,
        renderType: "list",
        structuredData: actionNames
      });
    }
  }

  return { typeName: "Datasource", name: str(data.Name ?? data.name ?? ""), fields };
}

function extractWorkflowFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const displayName = str(data.DisplayName ?? data.displayName ?? "");
  if (displayName)
    fields.push({
      key: "displayName",
      label: "Display Name",
      value: displayName,
      renderType: "text",
      linkedGroup: "json"
    });

  const inactived = data.Inactived ?? data.inactived;
  if (inactived != null)
    fields.push({
      key: "status",
      label: "Status",
      value: inactived ? "Inactive" : "Active",
      renderType: "badge",
      badgeHue: inactived ? "red" : "green",
      linkedGroup: "json"
    });

  const jsonField = data.Json ?? data.json;
  const wfModel = parseJsonField(jsonField);
  if (wfModel && !Array.isArray(wfModel)) {
    const wf = wfModel as Record<string, unknown>;
    if (wf.Description ?? wf.description) {
      fields.push({
        key: "description",
        label: "Description",
        value: str(wf.Description ?? wf.description),
        renderType: "text",
        linkedGroup: "json"
      });
    }

    const phases = wf.HappyPathDefinition ?? wf.happyPathDefinition;
    if (Array.isArray(phases)) {
      const phaseNames = phases
        .map((p: Record<string, unknown>) => str(p.Name ?? p.name ?? p.DisplayName ?? p.displayName ?? "Phase"))
        .filter(Boolean);
      fields.push({
        key: "phases",
        label: "Phases",
        value: `${phases.length} phase(s)`,
        renderType: "list",
        structuredData: phaseNames,
        linkedGroup: "json"
      });
    }

    const nodes = wf.Nodes ?? wf.nodes;
    if (nodes && typeof nodes === "object") {
      const n = nodes as Record<string, unknown>;
      const steps = Array.isArray(n.Steps ?? n.steps) ? ((n.Steps ?? n.steps) as Record<string, unknown>[]) : [];
      const decisions = Array.isArray(n.Decisions ?? n.decisions)
        ? ((n.Decisions ?? n.decisions) as Record<string, unknown>[])
        : [];
      const gateways = Array.isArray(n.Gateways ?? n.gateways)
        ? ((n.Gateways ?? n.gateways) as Record<string, unknown>[])
        : [];
      const events = Array.isArray(n.Events ?? n.events) ? ((n.Events ?? n.events) as Record<string, unknown>[]) : [];
      const nodeNames: string[] = [
        ...steps.map((s) => `Step: ${str(s.Name ?? s.name ?? "unnamed")}`),
        ...decisions.map((d) => `Decision: ${str(d.Name ?? d.name ?? "unnamed")}`),
        ...gateways.map((g) => `Gateway: ${str(g.Name ?? g.name ?? "unnamed")}`),
        ...events.map((e) => `Event: ${str(e.Name ?? e.name ?? "unnamed")}`)
      ];
      if (nodeNames.length > 0)
        fields.push({
          key: "nodes",
          label: "Nodes",
          value: `${nodeNames.length} node(s)`,
          renderType: "list",
          structuredData: nodeNames,
          linkedGroup: "json"
        });
    }

    const params = wf.Parameters ?? wf.parameters;
    if (Array.isArray(params) && params.length > 0) {
      const paramRows = params
        .map((p: Record<string, unknown>) => ({
          name: str(p.Name ?? p.name ?? ""),
          type: str(p.Type ?? p.type ?? ""),
          required: p.IsRequired === true || p.isMandatory === true,
          defaultValue: str(p.DefaultValue ?? p.defaultValue ?? "")
        }))
        .filter((r: ParamRow) => r.name);
      fields.push({
        key: "wfParams",
        label: "Parameters",
        value: `${paramRows.length} parameter(s)`,
        renderType: "params-table",
        structuredData: paramRows,
        linkedGroup: "json"
      });
    }
  }

  return { typeName: "Workflow", name: str(data.Name ?? data.name ?? ""), fields };
}

function extractApiManagementFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const jsonField = data.Json ?? data.json;
  const apiDef = parseJsonField(jsonField);
  if (apiDef && !Array.isArray(apiDef)) {
    const api = apiDef as Record<string, unknown>;
    const endpoints = api.endpoints ?? api.Endpoints ?? api.paths ?? api.Paths;
    if (Array.isArray(endpoints)) {
      const listing = endpoints
        .slice(0, 10)
        .map((ep: Record<string, unknown>) => {
          const method = str(ep.method ?? ep.Method ?? ep.httpMethod ?? "").toUpperCase();
          const path = str(ep.path ?? ep.Path ?? ep.url ?? "");
          return method && path ? `${method} ${path}` : str(ep.name ?? ep.Name ?? "");
        })
        .filter(Boolean);
      fields.push({
        key: "endpoints",
        label: "Endpoints",
        value: `${endpoints.length} endpoint(s)`,
        renderType: "list",
        structuredData: listing,
        linkedGroup: "json"
      });
    }
  }

  return { typeName: "API Management", name: str(data.Name ?? data.name ?? ""), fields };
}

function extractScheduledJobFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const jsonField = data.Json ?? data.json;
  const jobDef = parseJsonField(jsonField);
  if (jobDef && !Array.isArray(jobDef)) {
    const job = jobDef as Record<string, unknown>;
    const cron = str(job.cronExpression ?? job.CronExpression ?? job.schedule ?? job.Schedule ?? "");
    if (cron)
      fields.push({
        key: "schedule",
        label: "Schedule",
        value: cron,
        renderType: "badge",
        badgeHue: "purple",
        linkedGroup: "json"
      });
    const target = str(job.actionName ?? job.ActionName ?? job.targetAction ?? job.TargetAction ?? "");
    if (target)
      fields.push({
        key: "targetAction",
        label: "Target Action",
        value: target,
        renderType: "text",
        linkedGroup: "json"
      });
  }

  return { typeName: "Scheduled Job", name: str(data.Name ?? data.name ?? ""), fields };
}

function extractOfflineTableFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const withData = data.WithData ?? data.withData;
  if (withData != null)
    fields.push({
      key: "withData",
      label: "Include Data",
      value: withData ? "Yes" : "No",
      renderType: "badge",
      badgeHue: withData ? "green" : "grey"
    });

  const editing = data.EnableEditing ?? data.enableEditing;
  if (editing != null)
    fields.push({
      key: "editing",
      label: "Editing Enabled",
      value: editing ? "Yes" : "No",
      renderType: "badge",
      badgeHue: editing ? "green" : "grey"
    });

  const where = str(data.WhereCriteria ?? data.whereCriteria ?? "");
  if (where) fields.push({ key: "where", label: "Where Criteria", value: where, renderType: "code", language: "sql" });

  const sendCriteria = str(data.WillSendRecordsCriteria ?? data.willSendRecordsCriteria ?? "");
  if (sendCriteria)
    fields.push({
      key: "sendCriteria",
      label: "Send Records Criteria",
      value: sendCriteria,
      renderType: "code",
      language: "sql"
    });

  return {
    typeName: "Offline Table",
    name: str(data.TableName ?? data.tableName ?? data.Name ?? data.name ?? ""),
    fields
  };
}

function extractProjectFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const desc = str(data.Description ?? data.description ?? "");
  if (desc) fields.push({ key: "description", label: "Description", value: desc, renderType: "text" });

  const schema = str(data.SchemaName ?? data.schemaName ?? "");
  if (schema) fields.push({ key: "schema", label: "Schema", value: schema, renderType: "badge", badgeHue: "blue" });

  const isPrivate = data.IsPrivate ?? data.isPrivate;
  if (isPrivate != null)
    fields.push({
      key: "private",
      label: "Private",
      value: isPrivate ? "Yes" : "No",
      renderType: "badge",
      badgeHue: isPrivate ? "red" : "green"
    });

  return { typeName: "Project", name: str(data.Name ?? data.name ?? ""), fields };
}

function extractNotificationFields(data: Record<string, unknown>): ExtractedFields {
  const fields: ExtractedField[] = [];

  const templateType = str(data.TemplateType ?? data.templateType ?? "");
  if (templateType)
    fields.push({
      key: "templateType",
      label: "Template Type",
      value: templateType,
      renderType: "badge",
      badgeHue: "blue"
    });

  const multiLang = data.MultiLanguageEnabled ?? data.multiLanguageEnabled;
  if (multiLang != null)
    fields.push({
      key: "multiLang",
      label: "Multi-Language",
      value: multiLang ? "Yes" : "No",
      renderType: "badge",
      badgeHue: multiLang ? "green" : "grey"
    });

  return { typeName: "Notification", name: str(data.Name ?? data.name ?? ""), fields };
}

/** Fields that are internal/noisy and should be hidden from the diff view. */
const HIDDEN_FIELD_NAMES = new Set([
  "id",
  "Id",
  "ID",
  "createdAt",
  "CreatedAt",
  "updatedAt",
  "UpdatedAt",
  "modifiedAt",
  "ModifiedAt",
  "createdBy",
  "CreatedBy",
  "updatedBy",
  "UpdatedBy",
  "timestamp",
  "Timestamp",
  "version",
  "Version",
  "isDeleted",
  "IsDeleted",
  "kProject_Id",
  "KProject_Id",
  "projectId",
  "ProjectId",
  // System / audit / serialization fields that are noise in a user-facing diff.
  "CreatedUser_Id",
  "createdUser_Id",
  "CreatedUserId",
  "createdUserId",
  "Domain_Id",
  "domain_Id",
  "DomainId",
  "domainId",
  "KDatasource_Id",
  "kDatasource_Id",
  "IsDbAction",
  "isDbAction",
  "IsManagedDbAction",
  "isManagedDbAction",
  "IsTagDisabled",
  "isTagDisabled",
  "AiActionStatus",
  "aiActionStatus",
  "GenericSelectDataJson",
  "genericSelectDataJson",
  "Mapping",
  "mapping",
  "TypeSystemIdentifier",
  "typeSystemIdentifier",
  "ReturnType",
  "returnType",
  "ReturnTypeName",
  "returnTypeName",
  "ReturnCardinality",
  "returnCardinality",
  "VersionName",
  "versionName",
  "DataModelEffectiveVersion",
  "dataModelEffectiveVersion"
]);

/** Extract ALL top-level properties from a snapshot, with semantic fields getting rich rendering. */
function extractAllFields(data: Record<string, unknown>, semanticFields: ExtractedField[]): ExtractedField[] {
  const semanticKeys = new Set(semanticFields.map((f) => f.key));
  const result = [...semanticFields];

  for (const [rawKey, rawValue] of Object.entries(data)) {
    if (HIDDEN_FIELD_NAMES.has(rawKey)) continue;
    if (rawKey === "Name" || rawKey === "name") continue; // Already shown in header

    // Generate a stable key from the property name
    const fieldKey = rawKey.charAt(0).toLowerCase() + rawKey.slice(1);
    if (semanticKeys.has(fieldKey)) continue;

    // Skip null/undefined/empty
    if (rawValue == null) continue;
    const strVal = typeof rawValue === "object" ? JSON.stringify(rawValue) : String(rawValue);
    if (!strVal || strVal === "{}" || strVal === "[]" || strVal === '""') continue;

    // Humanize the key: "ActionType" → "Action Type", "kDatasource_Id" → "Datasource Id"
    const label = rawKey
      .replace(/^k/i, "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase())
      .trim();

    const isLong = strVal.length > 200;
    const isJson = typeof rawValue === "object";

    result.push({
      key: fieldKey,
      label,
      value: isJson ? JSON.stringify(rawValue, null, 2) : strVal,
      renderType: isLong || isJson ? "code" : "text",
      language: isJson ? "json" : undefined
    });
  }

  return result;
}

function extractFields(
  entityType: number,
  data: Record<string, unknown>,
  allStaged?: StagedEntity[]
): ExtractedFields | null {
  let base: ExtractedFields | null;
  switch (entityType) {
    case EJsonEntityType.KAction:
      base = extractActionFields(data, allStaged);
      break;
    case EJsonEntityType.KDatasource:
      base = extractDatasourceFields(data, allStaged);
      break;
    case EJsonEntityType.KWorkFlow:
      base = extractWorkflowFields(data);
      break;
    case EJsonEntityType.KApiManagement:
      base = extractApiManagementFields(data);
      break;
    case EJsonEntityType.KScheduledJobs:
      base = extractScheduledJobFields(data);
      break;
    case EJsonEntityType.KOfflineTable:
      base = extractOfflineTableFields(data);
      break;
    case EJsonEntityType.KProject:
      base = extractProjectFields(data);
      break;
    case EJsonEntityType.KNotificationTemplate:
      base = extractNotificationFields(data);
      break;
    default:
      base = { typeName: "Entity", name: str(data.Name ?? data.name ?? ""), fields: [] };
      break;
  }

  if (!base) return null;

  // Augment with all remaining properties from the snapshot
  base.fields = extractAllFields(data, base.fields);
  return base;
}

// ── Public API: build diff fields from snapshots ──

export interface ExtractedEntity {
  typeName: string;
  name: string;
  parentInfo?: string;
}

/** Extract fields for staged changes (before/after). */
export function extractStagedDiffFields(
  entityType: number,
  beforeSnapshot: unknown,
  afterSnapshot: unknown,
  allStaged?: StagedEntity[]
): { entity: ExtractedEntity; fields: EntityDiffField[] } | null {
  const beforeData = parseSnapshot(beforeSnapshot);
  const afterData = parseSnapshot(afterSnapshot);
  if (!beforeData && !afterData) return null;

  const primary = afterData ?? beforeData;
  const extracted = extractFields(entityType, primary!, allStaged);
  if (!extracted) return null;

  const beforeExtracted = beforeData ? extractFields(entityType, beforeData, allStaged) : null;
  const afterExtracted = afterData ? extractFields(entityType, afterData, allStaged) : null;

  const allKeys = new Set<string>();
  beforeExtracted?.fields.forEach((f) => allKeys.add(f.key));
  afterExtracted?.fields.forEach((f) => allKeys.add(f.key));

  const fields: EntityDiffField[] = [];
  for (const key of allKeys) {
    const bf = beforeExtracted?.fields.find((f) => f.key === key);
    const af = afterExtracted?.fields.find((f) => f.key === key);
    const ref = af ?? bf!;

    let changeStatus: EntityDiffField["changeStatus"];
    if (!bf) changeStatus = "added";
    else if (!af) changeStatus = "removed";
    else if (bf.value !== af.value) changeStatus = "modified";
    else changeStatus = "unchanged";

    fields.push({
      key,
      label: ref.label,
      beforeValue: bf?.value,
      afterValue: af?.value,
      renderType: ref.renderType,
      language: ref.language,
      linkedGroup: ref.linkedGroup,
      changeStatus,
      badgeHue: ref.badgeHue,
      beforeData: bf?.structuredData,
      afterData: af?.structuredData
    });
  }

  return {
    entity: { typeName: extracted.typeName, name: extracted.name, parentInfo: extracted.parentInfo },
    fields
  };
}

/** Extract fields for merge conflicts (base/ours/theirs). */
export function extractMergeDiffFields(
  entityType: number,
  baseSnapshot: unknown,
  oursSnapshot: unknown,
  theirsSnapshot: unknown,
  allStaged?: StagedEntity[]
): { entity: ExtractedEntity; fields: EntityDiffField[] } | null {
  const baseData = parseSnapshot(baseSnapshot);
  const oursData = parseSnapshot(oursSnapshot);
  const theirsData = parseSnapshot(theirsSnapshot);
  if (!baseData && !oursData && !theirsData) return null;

  const primary = oursData ?? theirsData ?? baseData;
  const extracted = extractFields(entityType, primary!, allStaged);
  if (!extracted) return null;

  const baseExtracted = baseData ? extractFields(entityType, baseData, allStaged) : null;
  const oursExtracted = oursData ? extractFields(entityType, oursData, allStaged) : null;
  const theirsExtracted = theirsData ? extractFields(entityType, theirsData, allStaged) : null;

  const allKeys = new Set<string>();
  baseExtracted?.fields.forEach((f) => allKeys.add(f.key));
  oursExtracted?.fields.forEach((f) => allKeys.add(f.key));
  theirsExtracted?.fields.forEach((f) => allKeys.add(f.key));

  const fields: EntityDiffField[] = [];
  for (const key of allKeys) {
    const bField = baseExtracted?.fields.find((f) => f.key === key);
    const oField = oursExtracted?.fields.find((f) => f.key === key);
    const tField = theirsExtracted?.fields.find((f) => f.key === key);
    const ref = oField ?? tField ?? bField!;

    const oursChanged = bField?.value !== oField?.value;
    const theirsChanged = bField?.value !== tField?.value;

    let changeStatus: EntityDiffField["changeStatus"];
    if (oursChanged && theirsChanged && oField?.value !== tField?.value) changeStatus = "conflict";
    else if (oursChanged || theirsChanged) changeStatus = "modified";
    else changeStatus = "unchanged";

    fields.push({
      key,
      label: ref.label,
      baseValue: bField?.value,
      oursValue: oField?.value,
      theirsValue: tField?.value,
      renderType: ref.renderType,
      language: ref.language,
      linkedGroup: ref.linkedGroup,
      changeStatus,
      badgeHue: ref.badgeHue,
      baseData: bField?.structuredData,
      oursData: oField?.structuredData,
      theirsData: tField?.structuredData
    });
  }

  return {
    entity: { typeName: extracted.typeName, name: extracted.name, parentInfo: extracted.parentInfo },
    fields
  };
}
